import createSagaMiddleware from "@redux-saga/core";
import {applyMiddleware, combineReducers, compose, createStore} from "redux";
import {crashReporter, logger} from "../../../store";
import {
    configFormValid,
    configFormVisited
} from "../reducers/configFormValid.reducer";
import {
    configurationsList,
    configurationsListLoading
} from "../reducers/configurationsList.reducer";
import {denominationsList} from "../reducers/denominations.reducer";
import {
    editingConfiguration,
    editingConfigurationLoading,
    submitingConfigurationLoading,
    submitingConfigurationState,
    uploadedConfiguration,
    existingConfiguration
} from "../reducers/editingConfiguration.reducer";
import {gameInstances, gameBundles} from "../reducers/gameInstances.reducer";
import {
    schema,
    schemaLoading,
    progressiveRules
} from "../reducers/schema.reducer";
import {terminalGroupsReducer} from "../reducers/terminalGroups.reducer";
import {gameGroupsReducer} from "../reducers/gameGroups.reducer";
import {sounds} from "../reducers/sounds.reducer";
import {rtpProfiles} from "../reducers/rtpProfiles.reducer";
import {regions, regionsLoading} from "../reducers/regions.reducer";
import {megapotServers, megapotServersLoading} from "../reducers/megapotServer.reducer";
import {megapotProviders, megapotProvidersLoading} from "../reducers/megapotProviders.reducer";
import {
    mailingLists,
    mailingListsLoading
} from "../reducers/mailingLists.reducer";
import rootSaga from "../sagas";
import {currenciesList} from "../reducers/currencies.reducer";
import {productionServer, productionServerLoading} from "../reducers/productionServer.reducer";

export const configurationsRootReducer = combineReducers({
    configurationsList,
    configurationsListLoading,
    schema,
    schemaLoading,
    editingConfiguration,
    editingConfigurationLoading,
    submitingConfigurationLoading,
    submitingConfigurationState,
    configFormValid,
    configFormVisited,
    denominationsList,
    gameInstances,
    gameBundles,
    terminalGroups: terminalGroupsReducer,
    gameGroups: gameGroupsReducer,
    sounds,
    rtpProfiles,
    progressiveRules,
    uploadedConfiguration,
    existingConfiguration,
    regions,
    regionsLoading,
    mailingListsLoading,
    mailingLists,
    currenciesList,
    productionServer,
    productionServerLoading,
    megapotServers,
    megapotServersLoading,
    megapotProviders,
    megapotProvidersLoading,
});

const composeEnhancers = window["__REDUX_DEVTOOLS_EXTENSION_COMPOSE__"]
    ? window["__REDUX_DEVTOOLS_EXTENSION_COMPOSE__"]({
        name: "Configurations Store"
    })
    : compose;

const sagaMiddleware = createSagaMiddleware();

export const configurationsStore = createStore(
    configurationsRootReducer,
    {},
    composeEnhancers(applyMiddleware(sagaMiddleware, logger, crashReporter))
);

export const configurationsStoreSelectors = {
    configurationsList: (state = configurationsStore.getState()) =>
        state.configurationsList,

    configurationsListLoading: (state = configurationsStore.getState()) =>
        state.configurationsListLoading,

    schema: (state = configurationsStore.getState()) => state.schema,

    settingsSchema: (state = configurationsStore.getState()) =>
        state.schema.components.settings.schema,

    accountingSchema: (state = configurationsStore.getState()) =>
        state.schema.components.accounting.schema,

    groupsSchema: (state = configurationsStore.getState()) =>
        state.schema.components.jackpot_groups.schema.properties
            .terminal_groups,

    gameGroupsSchema: (state = configurationsStore.getState()) =>
        state.schema.components.jackpot_groups.schema.properties.game_groups,

    specialPrizesSchema: (state = configurationsStore.getState()) =>
        state.schema.components.special_prizes.schema,

    mrChampionSchema: (state = configurationsStore.getState()) =>
        state.schema.components.special_prizes.schema.definitions.$mr_champion,

    pigBoomSchema: (state = configurationsStore.getState()) =>
        state.schema.components.special_prizes.schema.definitions.$pig_boom,

    progressivesSchema: (state = configurationsStore.getState()) =>
        state.schema.components.progressives.schema,

    globalJackpotSchema: (state = configurationsStore.getState()) =>
        state.schema.components.global_jackpot.schema,

    mailSenderSchema: (state = configurationsStore.getState()) =>
        state.schema.components.email.schema,

    megapotSchema: (state = configurationsStore.getState()) =>
        state.schema.components.megapot.schema,

    schemaLoading: (state = configurationsStore.getState()) =>
        state.schemaLoading,

    editingConfiguration: (state = configurationsStore.getState()) =>
        state.editingConfiguration,

    existingConfiguration: (state = configurationsStore.getState()) =>
        state.existingConfiguration,

    editingConfigurationLoading: (state = configurationsStore.getState()) =>
        state.editingConfigurationLoading,

    submitingConfigurationLoading: (state = configurationsStore.getState()) =>
        state.submitingConfigurationLoading,

    submitingConfigurationState: (state = configurationsStore.getState()) =>
        state.submitingConfigurationState,

    basicConfig: (state = configurationsStore.getState()) => ({
        name: state.editingConfiguration.name,
        address_line_1: state.editingConfiguration.address_line_1,
        address_line_2: state.editingConfiguration.address_line_2,
        country: state.editingConfiguration.subsidiary
            ? state.editingConfiguration.subsidiary.country.code
            : null
    }),

    settingsConfig: (state = configurationsStore.getState()) =>
        state.editingConfiguration.settings &&
        state.editingConfiguration.settings.settings &&
        state.editingConfiguration.settings.settings.settings
            ? {
                ...state.editingConfiguration.settings.settings.settings
            }
            : null,

    accountingConfig: (state = configurationsStore.getState()) =>
        state.editingConfiguration.settings &&
        state.editingConfiguration.settings.settings &&
        state.editingConfiguration.settings.settings.accounting
            ? {
                ...state.editingConfiguration.settings.settings.accounting
            }
            : null,

    groupsConfig: (state = configurationsStore.getState()) =>
        state.terminalGroups.terminalGroups,

    gameGroupsConfig: (state = configurationsStore.getState()) =>
        state.gameGroups.gameGroups,

    gameGroups: (state = configurationsStore.getState()) =>
        state.gameGroups.gameGroups,

    selectedGameGroups: (state = configurationsStore.getState()) =>
        state.gameGroups.selected,

    specialPrizesConfig: (state = configurationsStore.getState()) =>
        state.editingConfiguration.settings &&
        state.editingConfiguration.settings.settings &&
        state.editingConfiguration.settings.settings.special_prizes
            ? [...state.editingConfiguration.settings.settings.special_prizes]
            : [],

    mrChampionConfig: (state = configurationsStore.getState()) =>
        state.editingConfiguration.settings &&
        state.editingConfiguration.settings.settings &&
        state.editingConfiguration.settings.settings.special_prizes
            ? [
                ...state.editingConfiguration.settings.settings.special_prizes.filter(
                    item => item && item.type === "mr_champion"
                )
            ]
            : [],

    pigBoomConfig: (state = configurationsStore.getState()) =>
        state.editingConfiguration.settings &&
        state.editingConfiguration.settings.settings &&
        state.editingConfiguration.settings.settings.special_prizes
            ? [
                ...state.editingConfiguration.settings.settings.special_prizes.filter(
                    item => item && item.type === "pig_boom"
                )
            ]
            : [],

    progressivesConfig: (state = configurationsStore.getState()) =>
        state.editingConfiguration.settings &&
        state.editingConfiguration.settings.settings &&
        state.editingConfiguration.settings.settings.progressives
            ? [...state.editingConfiguration.settings.settings.progressives]
            : [],

    globalJackpotConfig: (state = configurationsStore.getState()) =>
        state.editingConfiguration.settings &&
        state.editingConfiguration.settings.settings &&
        state.editingConfiguration.settings.settings.global_jackpot
            ? {...state.editingConfiguration.settings.settings.global_jackpot}
            : {},

    mailSenderConfig: (state = configurationsStore.getState()) =>
        state.editingConfiguration.settings &&
        state.editingConfiguration.settings.settings &&
        state.editingConfiguration.settings.settings.email
            ? {
                ...state.editingConfiguration.settings.settings.email
            }
            : null,

    megapotConfig: (state = configurationsStore.getState()) =>
        state.editingConfiguration.settings &&
        state.editingConfiguration.settings.settings &&
        state.editingConfiguration.settings.settings.megapot
            ? {
                ...state.editingConfiguration.settings.settings.megapot
            }
            : {},

    configFormValid: (state = configurationsStore.getState()) =>
        state.configFormValid,

    configFormVisited: (state = configurationsStore.getState()) =>
        state.configFormVisited,

    denominationsList: (state = configurationsStore.getState()) =>
        state.denominationsList,

    currenciesList: (state = configurationsStore.getState()) =>
        state.currenciesList,

    gameInstances: (state = configurationsStore.getState()) =>
        state.gameInstances,

    selectedGroupType: (state = configurationsStore.getState()) => {
        try {
            return state.editingConfiguration.settings.selectedGroupType;
        } catch (_error) {
            return null;
        }
    },

    getSelectedGroupType: (state = configurationsStore.getState()) =>
        state.editingConfiguration.settings.selectedGroupType,

    terminalGroups: (state = configurationsStore.getState()) =>
        state.terminalGroups.terminalGroups,

    selectedTerminalGroups: (state = configurationsStore.getState()) =>
        state.terminalGroups.selected,

    selectedGameInstances: (state = configurationsStore.getState()) =>
        state.gameInstances.selected,

    soundDecibels: (state = configurationsStore.getState()) =>
        state.sounds.decibels,

    soundPercentage: (state = configurationsStore.getState()) =>
        state.sounds.percentage,

    sounds: (state = configurationsStore.getState()) => state.sounds.settings,

    soundsOrder: (state = configurationsStore.getState()) => state.sounds.order,

    soundGroups: (state = configurationsStore.getState()) =>
        state.sounds.groups,

    rtpProfiles: (state = configurationsStore.getState()) =>
        state.rtpProfiles.profiles,

    rtpDefaultProfile: (state = configurationsStore.getState()) =>
        state.rtpProfiles.defaultProfile,

    previousRTPDefaultProfile: (state = configurationsStore.getState()) =>
        state.rtpProfiles.previousDefaultProfile,

    rtpProfileSettings: (state = configurationsStore.getState()) =>
        state.rtpProfiles.settings,

    rtpProfilesOrder: (state = configurationsStore.getState()) =>
        state.rtpProfiles.order,

    rtpProfilesLoading: (state = configurationsStore.getState()) =>
        state.rtpProfiles.loading,

    progressiveRules: (state = configurationsStore.getState()) =>
        state.progressiveRules,

    uploadedConfiguration: (state = configurationsStore.getState()) =>
        state.uploadedConfiguration,

    regions: (state = configurationsStore.getState()) => state.regions,
    regionsLoading: (state = configurationsStore.getState()) =>
        state.regionsLoading,
    mailingLists: (state = configurationsStore.getState()) =>
        state.mailingLists,
    mailingListsLoading: (state = configurationsStore.getState()) =>
        state.mailingListsLoading,
    gameBundles: (state = configurationsStore.getState()) => state.gameBundles,
    productionServer: (state = configurationsStore.getState()) => state.productionServer,
    productionServerLoading: (state = configurationsStore.getState()) => state.productionServerLoading,
    megapotServers: (state = configurationsStore.getState()) => state.megapotServers,
    megapotServersLoading: (state = configurationsStore.getState()) => state.megapotServersLoading,
    megapotProviders: (state = configurationsStore.getState()) => state.megapotProviders,
    megapotProvidersLoading: (state = configurationsStore.getState()) => state.megapotProvidersLoading,
};

sagaMiddleware.run(rootSaga);
